import type {
  ScheduleEvent,
  ScheduleConfig,
  Resource,
  BackgroundEvent,
  CustomAttributeDefinition,
  HolidayDefinition,
  Client,
} from '@fulcrum/schedule-embedded';

export const DEMO_CLIENTS: Client[] = [
  { id: 'client-001', name: 'Alex Rivera' },
  { id: 'client-002', name: 'Jamie Chen' },
  { id: 'client-003', name: 'Morgan Davis' },
  { id: 'client-004', name: 'Taylor Kim' },
  { id: 'client-005', name: 'Jordan Patel' },
  { id: 'client-006', name: 'Casey Nguyen' },
  { id: 'client-007', name: 'Riley Thompson' },
  { id: 'client-008', name: 'Quinn Martinez' },
  { id: 'client-009', name: 'Avery Wilson' },
  { id: 'client-010', name: 'Skyler Brown' },
  { id: 'client-011', name: 'Peyton Garcia' },
  { id: 'client-012', name: 'Reese Anderson' },
];

// ─── Resources ─────────────────────────────────────────────────────────────

export const RESOURCES: Resource[] = [
  {
    id: 'alice',
    name: 'Alice Johnson',
    color: '#8B5CF6',
    customProperties: { department: 'Engineering', role: 'Lead Dev' },
    workingHours: { start: '08:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
  },
  {
    id: 'bob',
    name: 'Bob Martinez',
    color: '#10B981',
    customProperties: { department: 'Engineering', role: 'Backend' },
    workingHours: { start: '09:00', end: '18:00', daysOfWeek: [1, 2, 3, 4, 5] },
  },
  {
    id: 'carol',
    name: 'Carol Kim',
    color: '#F59E0B',
    customProperties: { department: 'Design', role: 'UX Lead' },
    workingHours: {
      start: '07:00',
      end: '16:00',
      daySchedule: [
        { dayOfWeek: 1, start: '07:00', end: '16:00' },
        { dayOfWeek: 2, start: '07:00', end: '16:00' },
        { dayOfWeek: 3, start: '07:00', end: '16:00' },
        { dayOfWeek: 4, start: '07:00', end: '16:00' },
        { dayOfWeek: 5, start: '07:00', end: '12:00' },
      ],
    },
  },
  { id: 'david',  name: 'David Chen',      color: '#EF4444', customProperties: { department: 'Engineering', role: 'Frontend' } },
  { id: 'emma',   name: 'Emma Wilson',     color: '#06B6D4', customProperties: { department: 'Product',     role: 'PM' } },
  { id: 'frank',  name: 'Frank Torres',    color: '#EC4899', customProperties: { department: 'Sales',       role: 'Account Exec' } },
  { id: 'grace',  name: 'Grace Lee',       color: '#84CC16', customProperties: { department: 'Marketing',   role: 'Strategist' } },
  { id: 'henry',  name: 'Henry Park',      color: '#F97316', customProperties: { department: 'Engineering', role: 'DevOps' } },
  { id: 'iris',   name: 'Iris Nakamura',   color: '#A78BFA', customProperties: { department: 'Design',      role: 'Visual' } },
  { id: 'jack',   name: 'Jack Thompson',   color: '#34D399', customProperties: { department: 'Sales',       role: 'SDR' } },
  { id: 'kate',   name: 'Kate Brown',      color: '#FCD34D', customProperties: { department: 'Product',     role: 'Analyst' } },
  { id: 'liam',   name: 'Liam Davis',      color: '#60A5FA', customProperties: { department: 'Engineering', role: 'Mobile' } },
  { id: 'maya',   name: 'Maya Patel',      color: '#F472B6', customProperties: { department: 'Marketing',   role: 'Content' } },
  { id: 'noah',   name: 'Noah Garcia',     color: '#4ADE80', customProperties: { department: 'Finance',     role: 'Controller' } },
  { id: 'olivia', name: 'Olivia Smith',    color: '#FB923C', customProperties: { department: 'HR',          role: 'Recruiter' } },
];

// ─── Custom attribute definitions ──────────────────────────────────────────

export const CUSTOM_ATTRIBUTE_DEFS: CustomAttributeDefinition[] = [
  {
    key: 'priority',
    label: 'Priority',
    type: 'select',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'critical', label: 'Critical' },
    ],
  },
  {
    key: 'tags',
    label: 'Tags',
    type: 'multiselect',
    options: [
      { value: 'internal', label: 'Internal' },
      { value: 'external', label: 'External' },
      { value: 'billable', label: 'Billable' },
      { value: 'review', label: 'Review' },
    ],
  },
  {
    key: 'budget',
    label: 'Budget ($)',
    type: 'number',
  },
  {
    key: 'dueDate',
    label: 'Due Date',
    type: 'date',
  },
];

// ─── US Federal Holidays (2026) ─────────────────────────────────────────────
// Fixed-date holidays use recurring: true (Jan 1, Jun 19, Jul 4, Nov 11, Dec 25).
// Floating holidays (third Monday, last Monday, etc.) use specific 2026 dates.

export const US_HOLIDAYS_2026: HolidayDefinition[] = [
  { name: "New Year's Day",            date: '2026-01-01', color: '#6366f1', recurring: true  },
  { name: 'MLK Jr. Day',              date: '2026-01-19', color: '#6366f1', recurring: false },
  { name: "Presidents' Day",           date: '2026-02-16', color: '#6366f1', recurring: false },
  { name: 'Memorial Day',              date: '2026-05-25', color: '#6366f1', recurring: false },
  { name: 'Juneteenth',                date: '2026-06-19', color: '#10B981', recurring: true  },
  { name: 'Independence Day',          date: '2026-07-04', color: '#EF4444', recurring: true  },
  { name: 'Labor Day',                 date: '2026-09-07', color: '#6366f1', recurring: false },
  { name: 'Columbus Day',              date: '2026-10-12', color: '#6366f1', recurring: false },
  { name: 'Veterans Day',              date: '2026-11-11', color: '#6366f1', recurring: true  },
  { name: 'Thanksgiving',              date: '2026-11-26', color: '#F59E0B', recurring: false },
  { name: 'Christmas Day',             date: '2026-12-25', color: '#EF4444', recurring: true  },
];

// ─── Background events ──────────────────────────────────────────────────────

export const BACKGROUND_EVENTS: BackgroundEvent[] = [
  {
    id: 'lunch',
    title: 'Lunch Break',
    color: '#FCD34D',
    opacity: 0.2,
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: '12:00',
    endTime: '13:00',
  },
  {
    id: 'standup-slot',
    title: 'Daily Standup Window',
    color: '#60A5FA',
    opacity: 0.15,
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: '09:00',
    endTime: '09:30',
  },
  {
    id: 'focus-morning',
    title: 'Focus Time',
    color: '#34D399',
    opacity: 0.12,
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: '10:00',
    endTime: '12:00',
  },
];

// ─── Wait list events ───────────────────────────────────────────────────────
// These events are defined but not yet placed on the schedule.
// Some carry original time hints (preserved duration), others are fully unscheduled.

export const QUEUED_EVENTS: ScheduleEvent[] = [
  {
    id: 'wl-1',
    title: 'Security Audit Kickoff',
    startUtc: thisWeekDay(0, 10, 0).toISOString(),
    endUtc:   thisWeekDay(0, 11, 0).toISOString(),
    color: '#EF4444',
    resourceId: 'alice',
    queued: true,
    customAttributes: [{ key: 'priority', value: 'critical' }],
  },
  {
    id: 'wl-2',
    title: 'Vendor Contract Review',
    startUtc: thisWeekDay(1, 14, 0).toISOString(),
    endUtc:   thisWeekDay(1, 15, 0).toISOString(),
    color: '#8B5CF6',
    resourceId: 'noah',
    queued: true,
    customAttributes: [{ key: 'tags', value: ['external', 'billable'] }],
  },
  {
    id: 'wl-3',
    title: 'Mobile App Discovery',
    startUtc: '',
    endUtc: '',
    color: '#06B6D4',
    resourceId: 'liam',
    queued: true,
    customAttributes: [{ key: 'priority', value: 'high' }],
  },
  {
    id: 'wl-4',
    title: 'Q4 Budget Alignment',
    startUtc: thisWeekDay(2, 9, 0).toISOString(),
    endUtc:   thisWeekDay(2, 10, 30).toISOString(),
    color: '#10B981',
    resourceId: 'emma',
    queued: true,
    customAttributes: [{ key: 'priority', value: 'high' }, { key: 'budget', value: 25000 }],
  },
  {
    id: 'wl-5',
    title: 'Accessibility Compliance Training',
    startUtc: '',
    endUtc: '',
    color: '#F59E0B',
    queued: true,
    customAttributes: [{ key: 'tags', value: ['internal'] }],
  },
  {
    id: 'wl-6',
    title: 'Partnership Negotiations',
    startUtc: thisWeekDay(3, 13, 0).toISOString(),
    endUtc:   thisWeekDay(3, 14, 0).toISOString(),
    color: '#EC4899',
    resourceId: 'frank',
    queued: true,
    customAttributes: [{ key: 'client', value: 'TechPartner Ltd' }, { key: 'tags', value: ['external'] }],
  },
  {
    id: 'wl-7',
    title: 'Infrastructure Upgrade Planning',
    startUtc: '',
    endUtc: '',
    color: '#F97316',
    resourceId: 'henry',
    queued: true,
    customAttributes: [{ key: 'priority', value: 'medium' }],
  },
];

// ─── Generate realistic sample events ─────────────────────────────────────

function todayAt(h: number, m = 0): Date {
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

function thisWeekDay(dayOffset: number, h: number, m = 0): Date {
  const d = new Date();
  const monday = new Date(d);
  const dow = d.getDay(); // 0=Sun
  const daysToMon = dow === 0 ? -6 : 1 - dow;
  monday.setDate(d.getDate() + daysToMon);
  monday.setDate(monday.getDate() + dayOffset);
  monday.setHours(h, m, 0, 0);
  return monday;
}

export function generateEvents(timezone: string): ScheduleEvent[] {
  void timezone;
  return [

    // ── Monday ──────────────────────────────────────────────────────────────

    {
      id: 'ev-mon-1',
      title: 'Daily Standup',
      startUtc: thisWeekDay(0, 9, 0).toISOString(),
      endUtc:   thisWeekDay(0, 9, 30).toISOString(),
      color: '#3B82F6', backgroundColor: '#3B82F6',
      resourceId: 'alice',
      recurrenceRule: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR',
      customAttributes: [{ key: 'priority', value: 'medium' }],
    },
    {
      // HTML: bold urgency prefix
      id: 'ev-mon-2',
      title: '<b>URGENT</b> — Client Escalation: Acme Corp',
      startUtc: thisWeekDay(0, 9, 30).toISOString(),
      endUtc:   thisWeekDay(0, 10, 30).toISOString(),
      color: '#EF4444', backgroundColor: '#EF4444',
      resourceId: 'alice',
      customAttributes: [
        { key: 'client', value: 'Acme Corp' },
        { key: 'priority', value: 'critical' },
        { key: 'tags', value: ['external', 'billable'] },
      ],
    },
    {
      id: 'ev-mon-3',
      title: 'Sprint Planning',
      startUtc: thisWeekDay(0, 10, 30).toISOString(),
      endUtc:   thisWeekDay(0, 12, 0).toISOString(),
      color: '#10B981', backgroundColor: '#10B981',
      resourceId: 'bob',
      location: 'Conference Room A',
      customAttributes: [{ key: 'priority', value: 'high' }, { key: 'tags', value: ['internal'] }],
    },
    {
      // HTML: inline colored version badge
      id: 'ev-mon-4',
      title: 'Release Planning — <b style="color:#86efac">v2.5.0</b>',
      startUtc: thisWeekDay(0, 13, 0).toISOString(),
      endUtc:   thisWeekDay(0, 14, 30).toISOString(),
      color: '#F97316', backgroundColor: '#F97316',
      resourceId: 'henry',
      customAttributes: [{ key: 'priority', value: 'high' }],
    },
    {
      id: 'ev-mon-5',
      title: 'UX Kickoff — Mobile Redesign',
      startUtc: thisWeekDay(0, 14, 0).toISOString(),
      endUtc:   thisWeekDay(0, 15, 30).toISOString(),
      color: '#F59E0B', backgroundColor: '#F59E0B',
      resourceId: 'carol',
      customAttributes: [{ key: 'tags', value: ['internal'] }],
    },
    {
      // HTML: bold CEO name + plain text
      id: 'ev-mon-6',
      title: '<b>CEO</b> Sync — Strategy Q3',
      startUtc: thisWeekDay(0, 15, 30).toISOString(),
      endUtc:   thisWeekDay(0, 16, 30).toISOString(),
      color: '#8B5CF6', backgroundColor: '#8B5CF6',
      resourceId: 'emma',
      customAttributes: [{ key: 'priority', value: 'high' }],
    },
    {
      id: 'ev-mon-7',
      title: 'Sales Pipeline Review',
      startUtc: thisWeekDay(0, 16, 30).toISOString(),
      endUtc:   thisWeekDay(0, 17, 30).toISOString(),
      color: '#EC4899', backgroundColor: '#EC4899',
      resourceId: 'frank',
      customAttributes: [{ key: 'tags', value: ['internal'] }],
    },

    // ── Tuesday ──────────────────────────────────────────────────────────────

    {
      id: 'ev-tue-1',
      title: 'Daily Standup',
      startUtc: thisWeekDay(1, 9, 0).toISOString(),
      endUtc:   thisWeekDay(1, 9, 30).toISOString(),
      color: '#3B82F6', backgroundColor: '#3B82F6',
      resourceId: 'bob',
    },
    {
      // HTML: warning icon + colored text for blocked status
      id: 'ev-tue-2',
      title: '<span style="color:#fde68a">⚠</span> <i>Blocked</i> — API Contract Review',
      startUtc: thisWeekDay(1, 9, 30).toISOString(),
      endUtc:   thisWeekDay(1, 10, 30).toISOString(),
      color: '#64748b', backgroundColor: '#64748b',
      resourceId: 'david',
      customAttributes: [{ key: 'priority', value: 'high' }, { key: 'tags', value: ['review'] }],
    },
    {
      id: 'ev-tue-3',
      title: 'Interview: Senior Frontend Engineer',
      startUtc: thisWeekDay(1, 10, 30).toISOString(),
      endUtc:   thisWeekDay(1, 12, 0).toISOString(),
      color: '#06B6D4', backgroundColor: '#06B6D4',
      resourceId: 'olivia',
      customAttributes: [{ key: 'priority', value: 'medium' }],
    },
    {
      id: 'ev-tue-4',
      title: 'Lunch & Learn — Accessibility Best Practices',
      startUtc: thisWeekDay(1, 12, 0).toISOString(),
      endUtc:   thisWeekDay(1, 13, 0).toISOString(),
      color: '#84CC16', backgroundColor: '#84CC16',
    },
    {
      // HTML: P0 badge + colored critical text
      id: 'ev-tue-5',
      title: '<b>P0</b> — <span style="color:#fca5a5">Production Incident</span>',
      startUtc: thisWeekDay(1, 13, 0).toISOString(),
      endUtc:   thisWeekDay(1, 14, 0).toISOString(),
      color: '#DC2626', backgroundColor: '#DC2626',
      resourceId: 'henry',
      customAttributes: [{ key: 'priority', value: 'critical' }],
    },
    {
      id: 'ev-tue-6',
      title: 'Marketing Campaign Debrief',
      startUtc: thisWeekDay(1, 14, 0).toISOString(),
      endUtc:   thisWeekDay(1, 15, 0).toISOString(),
      color: '#84CC16', backgroundColor: '#84CC16',
      resourceId: 'grace',
      customAttributes: [{ key: 'tags', value: ['internal'] }],
    },
    {
      // HTML: colored approval checkmark
      id: 'ev-tue-7',
      title: 'Design Review <b style="color:#6ee7b7">✓ Approved</b>',
      startUtc: thisWeekDay(1, 15, 0).toISOString(),
      endUtc:   thisWeekDay(1, 16, 0).toISOString(),
      color: '#A78BFA', backgroundColor: '#A78BFA',
      resourceId: 'iris',
      customAttributes: [{ key: 'tags', value: ['review'] }],
    },
    {
      id: 'ev-tue-8',
      title: '1:1 — Bob & Alice',
      startUtc: thisWeekDay(1, 16, 0).toISOString(),
      endUtc:   thisWeekDay(1, 16, 30).toISOString(),
      color: '#10B981', backgroundColor: '#10B981',
      resourceId: 'bob',
    },

    // ── Wednesday ────────────────────────────────────────────────────────────

    {
      id: 'ev-wed-1',
      title: 'Daily Standup',
      startUtc: thisWeekDay(2, 9, 0).toISOString(),
      endUtc:   thisWeekDay(2, 9, 30).toISOString(),
      color: '#3B82F6', backgroundColor: '#3B82F6',
      resourceId: 'alice',
    },
    {
      // HTML: bold + italic combined (All Hands)
      id: 'ev-wed-2',
      title: '<b><i>All Hands</i></b> — Q3 Results',
      startUtc: thisWeekDay(2, 9, 30).toISOString(),
      endUtc:   thisWeekDay(2, 11, 0).toISOString(),
      color: '#7C3AED', backgroundColor: '#7C3AED',
      location: 'Board Room',
      customAttributes: [{ key: 'priority', value: 'high' }, { key: 'tags', value: ['internal'] }],
    },
    {
      id: 'ev-wed-3',
      title: 'Architecture Review — Data Layer',
      startUtc: thisWeekDay(2, 11, 0).toISOString(),
      endUtc:   thisWeekDay(2, 12, 30).toISOString(),
      color: '#EF4444', backgroundColor: '#EF4444',
      resourceId: 'alice',
      customAttributes: [{ key: 'priority', value: 'high' }, { key: 'tags', value: ['internal'] }],
    },
    {
      id: 'ev-wed-4',
      title: 'Team Lunch',
      startUtc: thisWeekDay(2, 12, 30).toISOString(),
      endUtc:   thisWeekDay(2, 13, 30).toISOString(),
      color: '#EC4899', backgroundColor: '#EC4899',
    },
    {
      // HTML: candidate name in bold for interview context
      id: 'ev-wed-5',
      title: 'Interview: <b>Senior Backend Engineer</b>',
      startUtc: thisWeekDay(2, 13, 30).toISOString(),
      endUtc:   thisWeekDay(2, 15, 0).toISOString(),
      color: '#06B6D4', backgroundColor: '#06B6D4',
      resourceId: 'olivia',
      customAttributes: [{ key: 'priority', value: 'medium' }],
    },
    {
      id: 'ev-wed-6',
      title: 'Content Calendar Planning',
      startUtc: thisWeekDay(2, 15, 0).toISOString(),
      endUtc:   thisWeekDay(2, 16, 0).toISOString(),
      color: '#F472B6', backgroundColor: '#F472B6',
      resourceId: 'maya',
      customAttributes: [{ key: 'tags', value: ['internal'] }],
    },
    {
      // HTML: department color + text
      id: 'ev-wed-7',
      title: '<b style="color:#fde68a">Finance</b> — Budget Reconciliation',
      startUtc: thisWeekDay(2, 16, 0).toISOString(),
      endUtc:   thisWeekDay(2, 17, 30).toISOString(),
      color: '#4ADE80', backgroundColor: '#4ADE80',
      resourceId: 'noah',
      customAttributes: [{ key: 'budget', value: 12000 }, { key: 'tags', value: ['internal'] }],
    },

    // ── Thursday ─────────────────────────────────────────────────────────────

    {
      id: 'ev-thu-1',
      title: 'Daily Standup',
      startUtc: thisWeekDay(3, 9, 0).toISOString(),
      endUtc:   thisWeekDay(3, 9, 30).toISOString(),
      color: '#3B82F6', backgroundColor: '#3B82F6',
      resourceId: 'bob',
    },
    {
      id: 'ev-thu-2',
      title: 'Quarterly Planning',
      startUtc: thisWeekDay(3, 9, 30).toISOString(),
      endUtc:   thisWeekDay(3, 11, 30).toISOString(),
      color: '#8B5CF6', backgroundColor: '#8B5CF6',
      resourceId: 'carol',
      location: 'Conference Room B',
      customAttributes: [{ key: 'priority', value: 'high' }, { key: 'budget', value: 5000 }],
    },
    {
      // HTML: SLA warning with colored text
      id: 'ev-thu-3',
      title: '<span style="color:#fde68a">⚠</span> SLA Breach Review',
      startUtc: thisWeekDay(3, 11, 30).toISOString(),
      endUtc:   thisWeekDay(3, 12, 30).toISOString(),
      color: '#F97316', backgroundColor: '#F97316',
      resourceId: 'emma',
      customAttributes: [{ key: 'priority', value: 'critical' }, { key: 'client', value: 'GlobalTech' }],
    },
    {
      id: 'ev-thu-4',
      title: 'Investor Demo Prep',
      startUtc: thisWeekDay(3, 13, 0).toISOString(),
      endUtc:   thisWeekDay(3, 14, 30).toISOString(),
      color: '#0EA5E9', backgroundColor: '#0EA5E9',
      resourceId: 'frank',
      customAttributes: [{ key: 'priority', value: 'high' }, { key: 'tags', value: ['external'] }],
    },
    {
      // HTML: italic "phase" label with colored text
      id: 'ev-thu-5',
      title: 'Onboarding — <span style="color:#93c5fd"><i>Day 1</i></span> — Liam Davis',
      startUtc: thisWeekDay(3, 14, 30).toISOString(),
      endUtc:   thisWeekDay(3, 16, 0).toISOString(),
      color: '#60A5FA', backgroundColor: '#60A5FA',
      resourceId: 'liam',
    },
    {
      id: 'ev-thu-6',
      title: 'Code Review — Auth Module',
      startUtc: thisWeekDay(3, 16, 0).toISOString(),
      endUtc:   thisWeekDay(3, 17, 0).toISOString(),
      color: '#10B981', backgroundColor: '#10B981',
      resourceId: 'bob',
      customAttributes: [{ key: 'tags', value: ['review'] }],
    },
    {
      // HTML: bold version + checkmark deploy confirmation
      id: 'ev-thu-7',
      title: 'Hotfix Deploy <b style="color:#6ee7b7">✓ v2.4.1</b>',
      startUtc: thisWeekDay(3, 17, 0).toISOString(),
      endUtc:   thisWeekDay(3, 17, 30).toISOString(),
      color: '#14B8A6', backgroundColor: '#14B8A6',
      resourceId: 'henry',
      customAttributes: [{ key: 'priority', value: 'critical' }],
    },

    // ── Friday ───────────────────────────────────────────────────────────────

    {
      id: 'ev-fri-1',
      title: 'Daily Standup',
      startUtc: thisWeekDay(4, 9, 0).toISOString(),
      endUtc:   thisWeekDay(4, 9, 30).toISOString(),
      color: '#3B82F6', backgroundColor: '#3B82F6',
      resourceId: 'alice',
    },
    {
      id: 'ev-fri-2',
      title: 'Sprint Retrospective',
      startUtc: thisWeekDay(4, 9, 30).toISOString(),
      endUtc:   thisWeekDay(4, 11, 0).toISOString(),
      color: '#10B981', backgroundColor: '#10B981',
      location: 'Training Room',
      customAttributes: [{ key: 'tags', value: ['internal'] }],
    },
    {
      // HTML: star badge for demo day
      id: 'ev-fri-3',
      title: 'Sprint Demo <span style="color:#fde68a">★ Demo Day</span>',
      startUtc: thisWeekDay(4, 11, 0).toISOString(),
      endUtc:   thisWeekDay(4, 12, 30).toISOString(),
      color: '#7C3AED', backgroundColor: '#7C3AED',
      customAttributes: [{ key: 'priority', value: 'high' }, { key: 'tags', value: ['internal'] }],
    },
    {
      id: 'ev-fri-4',
      title: 'Team Lunch & Wrap-up',
      startUtc: thisWeekDay(4, 12, 30).toISOString(),
      endUtc:   thisWeekDay(4, 13, 30).toISOString(),
      color: '#EC4899', backgroundColor: '#EC4899',
    },
    {
      // HTML: bold department prefix
      id: 'ev-fri-5',
      title: '<b style="color:#f9a8d4">Marketing</b> — Campaign Launch Review',
      startUtc: thisWeekDay(4, 13, 30).toISOString(),
      endUtc:   thisWeekDay(4, 15, 0).toISOString(),
      color: '#84CC16', backgroundColor: '#84CC16',
      resourceId: 'grace',
      customAttributes: [{ key: 'tags', value: ['internal', 'review'] }],
    },
    {
      id: 'ev-fri-6',
      title: 'Backlog Grooming',
      startUtc: thisWeekDay(4, 15, 0).toISOString(),
      endUtc:   thisWeekDay(4, 16, 0).toISOString(),
      color: '#F59E0B', backgroundColor: '#F59E0B',
      resourceId: 'emma',
      customAttributes: [{ key: 'priority', value: 'medium' }],
    },
    {
      id: 'ev-fri-7',
      title: 'Team Happy Hour 🍕',
      startUtc: thisWeekDay(4, 16, 30).toISOString(),
      endUtc:   thisWeekDay(4, 18, 0).toISOString(),
      color: '#F97316', backgroundColor: '#F97316',
    },

    // ── Today (overlaid on whichever day it falls on) ────────────────────────

    {
      // HTML: italic blocked status to make it stand out
      id: 'ev-today-1',
      title: '<i>On Hold</i> — Vendor Contract Sign-off',
      startUtc: todayAt(8, 30).toISOString(),
      endUtc:   todayAt(9, 0).toISOString(),
      color: '#64748b', backgroundColor: '#64748b',
      resourceId: 'noah',
    },
    {
      id: 'ev-today-2',
      title: 'Bug Triage',
      startUtc: todayAt(11, 0).toISOString(),
      endUtc:   todayAt(11, 45).toISOString(),
      color: '#F97316', backgroundColor: '#F97316',
      customAttributes: [{ key: 'priority', value: 'high' }],
    },
    {
      // HTML: number in bold so priority jumps out
      id: 'ev-today-3',
      title: '<b>#1</b> Priority — Customer Health Check',
      startUtc: todayAt(14, 0).toISOString(),
      endUtc:   todayAt(15, 0).toISOString(),
      color: '#0EA5E9', backgroundColor: '#0EA5E9',
      resourceId: 'jack',
      customAttributes: [{ key: 'client', value: 'HealthCo' }, { key: 'tags', value: ['external', 'billable'] }],
    },
    {
      id: 'ev-today-4',
      title: 'Deployment Window',
      startUtc: todayAt(16, 30).toISOString(),
      endUtc:   todayAt(17, 0).toISOString(),
      color: '#14B8A6', backgroundColor: '#14B8A6',
      resourceId: 'henry',
      customAttributes: [{ key: 'priority', value: 'critical' }],
    },

    // ── Group Therapy Sessions ──────────────────────────────────────────────
    // These events carry groupId in their title prefix so the host can render
    // group capacity badges. The actual session metadata is returned via
    // onRequestGroupSessions; the events themselves are ordinary ScheduleEvents.

    {
      id: 'grp-mon-dbt',
      title: 'DBT Skills Group',
      startUtc: thisWeekDay(0, 10, 0).toISOString(),
      endUtc:   thisWeekDay(0, 11, 30).toISOString(),
      color: '#7C3AED', backgroundColor: '#7C3AED',
      resourceId: 'alice',
    },
    {
      id: 'grp-tue-trauma',
      title: 'Trauma Processing Group',
      startUtc: thisWeekDay(1, 14, 0).toISOString(),
      endUtc:   thisWeekDay(1, 15, 30).toISOString(),
      color: '#DC2626', backgroundColor: '#DC2626',
      resourceId: 'carol',
    },
    {
      id: 'grp-wed-anxiety',
      title: 'Anxiety Management Group',
      startUtc: thisWeekDay(2, 9, 0).toISOString(),
      endUtc:   thisWeekDay(2, 10, 0).toISOString(),
      color: '#0891B2', backgroundColor: '#0891B2',
      resourceId: 'eve',
    },
    {
      id: 'grp-wed-sub',
      title: 'Substance Use Recovery',
      startUtc: thisWeekDay(2, 13, 0).toISOString(),
      endUtc:   thisWeekDay(2, 14, 30).toISOString(),
      color: '#B45309', backgroundColor: '#B45309',
      resourceId: 'bob',
    },
    {
      id: 'grp-thu-cbt',
      title: 'CBT Psychoeducation Group',
      startUtc: thisWeekDay(3, 11, 0).toISOString(),
      endUtc:   thisWeekDay(3, 12, 0).toISOString(),
      color: '#059669', backgroundColor: '#059669',
      resourceId: 'henry',
    },
    {
      id: 'grp-fri-grief',
      title: 'Grief & Loss Support Group',
      startUtc: thisWeekDay(4, 10, 0).toISOString(),
      endUtc:   thisWeekDay(4, 11, 30).toISOString(),
      color: '#9333EA', backgroundColor: '#9333EA',
      resourceId: 'iris',
    },
  ];
}

// ─── Group session IDs (used by App.tsx to build demo onRequestGroupSessions) ──

export const GROUP_EVENT_IDS = [
  'grp-mon-dbt',
  'grp-tue-trauma',
  'grp-wed-anxiety',
  'grp-wed-sub',
  'grp-thu-cbt',
  'grp-fri-grief',
] as const;

export function generateGroupTherapyEvents(): ScheduleEvent[] {
  return [
    { id: 'grp-mon-dbt',    title: 'DBT Skills Group',            startUtc: thisWeekDay(0, 10, 0).toISOString(), endUtc: thisWeekDay(0, 11, 30).toISOString(), color: '#7C3AED', backgroundColor: '#7C3AED', resourceId: 'alice',  isGroupTherapy: true, groupCapacity: 12, groupType: 'DBT',           facilityType: 'in_person',  location: 'Conference Room A'    },
    { id: 'grp-tue-trauma', title: 'Trauma Processing Group',     startUtc: thisWeekDay(1, 14, 0).toISOString(), endUtc: thisWeekDay(1, 15, 30).toISOString(), color: '#DC2626', backgroundColor: '#DC2626', resourceId: 'carol',  isGroupTherapy: true, groupCapacity:  8, groupType: 'trauma',        facilityType: 'in_person',  location: 'Training Room'        },
    { id: 'grp-wed-anxiety',title: 'Anxiety Management Group',    startUtc: thisWeekDay(2,  9, 0).toISOString(), endUtc: thisWeekDay(2, 10,  0).toISOString(), color: '#0891B2', backgroundColor: '#0891B2', resourceId: 'eve',    isGroupTherapy: true, groupCapacity: 10, groupType: 'anxiety',       facilityType: 'telehealth', location: 'Remote / Video Call'  },
    { id: 'grp-wed-sub',    title: 'Substance Use Recovery',      startUtc: thisWeekDay(2, 13, 0).toISOString(), endUtc: thisWeekDay(2, 14, 30).toISOString(), color: '#B45309', backgroundColor: '#B45309', resourceId: 'bob',    isGroupTherapy: true, groupCapacity: 15, groupType: 'substance_use', facilityType: 'in_person',  location: 'Conference Room B'    },
    { id: 'grp-thu-cbt',    title: 'CBT Psychoeducation Group',   startUtc: thisWeekDay(3, 11, 0).toISOString(), endUtc: thisWeekDay(3, 12,  0).toISOString(), color: '#059669', backgroundColor: '#059669', resourceId: 'henry',  isGroupTherapy: true, groupCapacity: 12, groupType: 'CBT',           facilityType: 'hybrid',     location: 'Board Room'           },
    { id: 'grp-fri-grief',  title: 'Grief & Loss Support Group',  startUtc: thisWeekDay(4, 10, 0).toISOString(), endUtc: thisWeekDay(4, 11, 30).toISOString(), color: '#9333EA', backgroundColor: '#9333EA', resourceId: 'iris',   isGroupTherapy: true, groupCapacity: 10, groupType: 'grief',         facilityType: 'in_person',  location: 'Training Room'        },
  ];
}

// ─── Default config ─────────────────────────────────────────────────────────

export const DEMO_LOCATIONS: string[] = [
  'Conference Room A',
  'Conference Room B',
  'Training Room',
  'Board Room',
  'Remote / Video Call',
];

export function getDefaultConfig(includeResources: boolean): ScheduleConfig {
  return {
    timezone: 'America/Chicago',
    view: 'weekdays',
    timeRange: { start: '07:00', end: '19:00' },
    slotDuration: 30,
    workingHours: { start: '08:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
    resources: includeResources ? RESOURCES : undefined,
    backgroundEvents: BACKGROUND_EVENTS,
    customAttributeDefinitions: CUSTOM_ATTRIBUTE_DEFS,
    allowEventCreation: true,
    resourceGrouping: 'byDate',
    locations: DEMO_LOCATIONS,
  };
}
